import type {
  CongregationChantingRecord,
  CongregationEligibilityConfig,
  CongregationMeResponse,
  CongregationPersona,
  CongregationPersonaOption,
  CongregationProfileSnapshot,
  CongregationRoleAssignment,
} from '../types/congregation';
import { congregationPersonaLabels, congregationPersonaRoutes } from '../types/congregation';
import type { CongregationApplication } from '../types/congregation';

const STORAGE_KEY = 'congregation.activePersona';

const CONGREGATION_PERSONA_PREFIXES = {
  MENTOR: '/congregation/mentor',
  DEVOTEE: '/congregation/devotee',
  APPLICANT: '/congregation/apply',
};

const roleEquals = (role: CongregationRoleAssignment, match: string) =>
  role.role.toUpperCase() === match.toUpperCase();

export const hasExactRole = (
  roles: CongregationRoleAssignment[],
  exactRole: 'MENTOR' | 'FACILITATOR' | 'DEVOTEE' | 'ADMIN',
): boolean => {
  return roles.some((role) => role.role.toUpperCase() === exactRole.toUpperCase());
};

export const loadStoredPersona = (): CongregationPersona | null => {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (raw === 'APPLICANT' || raw === 'DEVOTEE' || raw === 'MENTOR') {
    return raw;
  }
  return null;
};

export const persistPersona = (persona: CongregationPersona) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, persona);
};

export const deriveAvailablePersonas = (
  me?: CongregationMeResponse,
): CongregationPersonaOption[] => {
  const options: CongregationPersonaOption[] = [];
  const roles = me?.roles ?? [];
  const member = me?.member ?? null;
  const status = member?.status?.toUpperCase() ?? null;
  const hasMentorRole = roles.some(
    (role) => roleEquals(role, 'MENTOR') || roleEquals(role, 'FACILITATOR'),
  );
  const hasDevoteeRole = roles.some((role) => roleEquals(role, 'DEVOTEE'));
  const hasMemberProfile = Boolean(member);

  const shouldExposeDevotee =
    hasMemberProfile && (status === 'ACTIVE' || status === 'INACTIVE' || hasDevoteeRole);

  const shouldExposeApplicant =
    !hasMemberProfile ||
    status === 'PENDING' ||
    status === 'REJECTED' ||
    status === 'WITHDRAWN' ||
    status === 'INACTIVE';

  if (shouldExposeApplicant) {
    options.push({ key: 'APPLICANT', label: congregationPersonaLabels.APPLICANT });
  }

  if (shouldExposeDevotee) {
    options.push({
      key: 'DEVOTEE',
      label: congregationPersonaLabels.DEVOTEE,
      description: status ? status.charAt(0) + status.slice(1).toLowerCase() : undefined,
    });
  }

  const canAccessMentor = hasMentorRole && status === 'ACTIVE';

  if (canAccessMentor) {
    options.push({ key: 'MENTOR', label: congregationPersonaLabels.MENTOR });
  }

  if (!options.length) {
    return [{ key: 'APPLICANT', label: congregationPersonaLabels.APPLICANT }];
  }

  return options;
};

export const selectDefaultPersona = (
  options: CongregationPersonaOption[],
  preferred?: CongregationPersona | null,
): CongregationPersona => {
  if (preferred && options.some((option) => option.key === preferred)) {
    return preferred;
  }

  const mentorOption = options.find((option) => option.key === 'MENTOR');
  const devoteeOption = options.find((option) => option.key === 'DEVOTEE');
  const applicantOption = options.find((option) => option.key === 'APPLICANT');

  return (mentorOption ?? devoteeOption ?? applicantOption ?? options[0]).key;
};

export const getPersonaFromPath = (pathname: string): CongregationPersona | null => {
  if (pathname.startsWith(CONGREGATION_PERSONA_PREFIXES.MENTOR)) return 'MENTOR';
  if (pathname.startsWith(CONGREGATION_PERSONA_PREFIXES.DEVOTEE)) return 'DEVOTEE';
  if (pathname.startsWith(CONGREGATION_PERSONA_PREFIXES.APPLICANT)) return 'APPLICANT';
  if (pathname === '/congregation') return null;
  return null;
};

export const getPersonaRoute = (persona: CongregationPersona): string =>
  congregationPersonaRoutes[persona];

export const describeMemberStatus = (status?: string | null): string | null => {
  if (!status) return null;
  const normalized = status.toUpperCase();

  switch (normalized) {
    case 'ACTIVE':
      return 'Active devotee';
    case 'INACTIVE':
      return 'Inactive — reach out to mentor or support';
    case 'PENDING':
      return 'Application pending mentor review';
    case 'REJECTED':
      return 'Application rejected — please review mentor guidance';
    case 'WITHDRAWN':
      return 'Application withdrawn';
    default:
      return status[0] + status.slice(1).toLowerCase();
  }
};

const APPLICATION_LOCKED_STATUSES = new Set([
  'PENDING',
  'SUBMITTED',
  'ASSIGNED_TO_MENTOR',
  'UNDER_REVIEW',
  'ACCEPTED',
]);

const APPLICATION_WITHDRAW_ELIGIBLE_STATUSES = new Set(['PENDING', 'SUBMITTED', 'ASSIGNED_TO_MENTOR', 'UNDER_REVIEW']);

export const isApplicationLockedStatus = (status?: string | null): boolean => {
  if (!status) return false;
  return APPLICATION_LOCKED_STATUSES.has(status.toUpperCase());
};

export const canWithdrawApplication = (status?: string | null): boolean => {
  if (!status) return false;
  return APPLICATION_WITHDRAW_ELIGIBLE_STATUSES.has(status.toUpperCase());
};

export const describeApplicationStatus = (status?: string | null): string => {
  if (!status) return 'Unknown';
  const normalized = status.toUpperCase();

  switch (normalized) {
    case 'SUBMITTED':
      return 'Submitted — awaiting review';
    case 'ASSIGNED_TO_MENTOR':
      return 'Assigned to mentor';
    case 'UNDER_REVIEW':
      return 'Under review';
    case 'ACCEPTED':
      return 'Accepted';
    case 'REJECTED':
      return 'Rejected';
    case 'WITHDRAWN':
      return 'Withdrawn';
    default:
      return status[0] + status.slice(1).toLowerCase();
  }
};

export type ApplicantProgressStage =
  | 'NOT_STARTED'
  | 'IN_PROGRESS'
  | 'SUBMITTED'
  | 'REVIEW'
  | 'ACCEPTED'
  | 'DECIDED'
  | 'WITHDRAWN';

export interface ApplicantProgressSummary {
  percent: number;
  label: string;
  stage: ApplicantProgressStage;
  color: 'neutral' | 'primary' | 'success' | 'warning' | 'danger';
}

const APPLICANT_PROGRESS_DEFAULT: ApplicantProgressSummary = {
  percent: 8,
  label: 'Not started yet',
  stage: 'NOT_STARTED',
  color: 'neutral',
};

const getApplicantProgressFromStatus = (status?: string | null): ApplicantProgressSummary => {
  if (!status) {
    return APPLICANT_PROGRESS_DEFAULT;
  }

  const normalized = status.toUpperCase();

  switch (normalized) {
    case 'DRAFT':
    case 'STARTED':
      return {
        percent: 32,
        label: 'Draft in progress',
        stage: 'IN_PROGRESS',
        color: 'primary',
      };
    case 'PENDING':
    case 'SUBMITTED':
      return {
        percent: 54,
        label: 'Submitted — awaiting mentor assignment',
        stage: 'SUBMITTED',
        color: 'primary',
      };
    case 'ASSIGNED_TO_MENTOR':
      return {
        percent: 68,
        label: 'Mentor assigned — review starting soon',
        stage: 'REVIEW',
        color: 'warning',
      };
    case 'UNDER_REVIEW':
      return {
        percent: 78,
        label: 'Mentor is reviewing your application',
        stage: 'REVIEW',
        color: 'warning',
      };
    case 'ACCEPTED':
      return {
        percent: 100,
        label: 'Accepted — welcome to the congregation',
        stage: 'ACCEPTED',
        color: 'success',
      };
    case 'REJECTED':
      return {
        percent: 100,
        label: 'Decision issued — please review mentor feedback',
        stage: 'DECIDED',
        color: 'danger',
      };
    case 'WITHDRAWN':
      return {
        percent: 20,
        label: 'Withdrawn — restart whenever you are ready',
        stage: 'WITHDRAWN',
        color: 'neutral',
      };
    default:
      return {
        percent: 48,
        label: normalized.charAt(0) + normalized.slice(1).toLowerCase(),
        stage: 'IN_PROGRESS',
        color: 'primary',
      };
  }
};

const MEMBERSHIP_STATUS_TO_APPLICATION_STATUS: Record<string, string> = {
  PENDING: 'SUBMITTED',
  ACTIVE: 'ACCEPTED',
  INACTIVE: 'ACCEPTED',
  REJECTED: 'REJECTED',
  WITHDRAWN: 'WITHDRAWN',
};

export const getApplicantProgressSummary = (
  applicationStatus?: string | null,
  membershipStatus?: string | null,
): ApplicantProgressSummary => {
  const status = applicationStatus ??
    (membershipStatus ? MEMBERSHIP_STATUS_TO_APPLICATION_STATUS[membershipStatus.toUpperCase()] : undefined);

  if (status) {
    return getApplicantProgressFromStatus(status);
  }

  if (membershipStatus) {
    const normalized = membershipStatus.toUpperCase();
    if (normalized === 'SUSPENDED') {
      return {
        percent: 100,
        label: 'Membership suspended — contact support',
        stage: 'DECIDED',
        color: 'danger',
      };
    }
  }

  return APPLICANT_PROGRESS_DEFAULT;
};

export const getLatestApplicationUpdate = (
  application: CongregationApplication,
): string | null => {
  const timestamp = application.updatedAt ?? application.createdAt ?? application.submittedAt;
  return timestamp ?? null;
};

export const formatIsoDate = (iso?: string | null, locale?: string): string => {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(locale ?? undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const formatIsoDateTime = (iso?: string | null, locale?: string): string => {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(locale ?? undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const MASK_CHAR = '•';

export const SENSITIVE_VALUE_PLACEHOLDER = 'Hidden for safeguarding';

export const maskPan = (value?: string | null): string => {
  if (!value) return SENSITIVE_VALUE_PLACEHOLDER;
  const trimmed = value.trim();
  if (!trimmed) return SENSITIVE_VALUE_PLACEHOLDER;
  if (/^[A-Z]{5}[0-9]{4}[A-Z]$/i.test(trimmed)) {
    const visibleStart = trimmed.slice(0, 3);
    const visibleEnd = trimmed.slice(-3);
    const maskedLength = Math.max(trimmed.length - (visibleStart.length + visibleEnd.length), 0);
    return `${visibleStart.toUpperCase()}${MASK_CHAR.repeat(maskedLength)}${visibleEnd.toUpperCase()}`;
  }
  return MASK_CHAR.repeat(Math.min(trimmed.length, 6));
};

export const maskPhone = (value?: string | null): string => {
  if (!value) return SENSITIVE_VALUE_PLACEHOLDER;
  const trimmed = value.trim();
  if (!trimmed) return SENSITIVE_VALUE_PLACEHOLDER;
  const digits = trimmed.match(/\d/g) ?? [];
  if (digits.length === 0) {
    return SENSITIVE_VALUE_PLACEHOLDER;
  }
  if (digits.length <= 4) {
    return trimmed;
  }
  const maskedDigits = digits.map((digit, index) => (index < digits.length - 4 ? MASK_CHAR : digit));
  let pointer = 0;
  let output = '';
  for (const char of trimmed) {
    if (/\d/.test(char)) {
      output += maskedDigits[pointer] ?? MASK_CHAR;
      pointer += 1;
    } else {
      output += char;
    }
  }
  return output;
};

export const maskEmail = (value?: string | null): string => {
  if (!value) return SENSITIVE_VALUE_PLACEHOLDER;
  const trimmed = value.trim();
  if (!trimmed) return SENSITIVE_VALUE_PLACEHOLDER;
  const atIndex = trimmed.indexOf('@');
  if (atIndex <= 0 || atIndex === trimmed.length - 1) {
    return SENSITIVE_VALUE_PLACEHOLDER;
  }
  const localPart = trimmed.slice(0, atIndex);
  const domain = trimmed.slice(atIndex + 1);
  if (!domain) return SENSITIVE_VALUE_PLACEHOLDER;
  if (localPart.length <= 1) {
    return `${localPart}${MASK_CHAR}@${domain}`;
  }
  const firstChar = localPart[0];
  const lastChar = localPart[localPart.length - 1];
  const middleMask = MASK_CHAR.repeat(Math.max(localPart.length - 2, 1));
  return `${firstChar}${middleMask}${lastChar}@${domain}`;
};

export const maskAddress = (value?: string | null): string => {
  if (!value) return SENSITIVE_VALUE_PLACEHOLDER;
  return SENSITIVE_VALUE_PLACEHOLDER;
};

export interface CongregationProfileCompletionSections {
  personal: number;
  contacts: number;
  addresses: number;
  family: number;
  devotional: number;
  documents: number;
  photos: number;
}

export interface CongregationProfileCompletion {
  overall: number;
  sections: CongregationProfileCompletionSections;
}

const clampPercentage = (value: number): number => {
  if (Number.isNaN(value) || !Number.isFinite(value)) {
    return 0;
  }
  if (value < 0) return 0;
  if (value > 100) return 100;
  return value;
};

export const calculateProfileCompletion = (
  profile?: CongregationProfileSnapshot | null,
): CongregationProfileCompletion => {
  if (!profile) {
    const emptySections: CongregationProfileCompletionSections = {
      personal: 0,
      contacts: 0,
      addresses: 0,
      family: 0,
      devotional: 0,
      documents: 0,
      photos: 0,
    };

    return {
      overall: 0,
      sections: emptySections,
    };
  }

  const member = profile.member;
  const contacts = profile.contacts ?? [];
  const addresses = profile.addresses ?? [];
  const family = profile.family ?? [];
  const chanting = profile.chanting ?? [];
  const books = profile.books ?? [];
  const courses = profile.courses ?? [];
  const milestones = profile.milestones ?? [];
  const documents = profile.documents ?? [];
  const photos = profile.photos ?? [];

  const personalScore = clampPercentage(member ? 100 : 0);
  const contactScore = clampPercentage(
    contacts.length
      ? (contacts.some((contact) => Boolean(contact.isPrimary)) ? 50 : 25) +
          (contacts.some((contact) => Boolean(contact.verifiedAt)) ? 50 : 0)
      : 0,
  );
  const addressScore = clampPercentage(addresses.length ? 100 : 0);
  const familyScore = clampPercentage(family.length ? 100 : 0);

  const devotionalParts = [chanting.length, books.length, courses.length, milestones.length];
  const devotionalScore = clampPercentage(
    devotionalParts.length
      ? (devotionalParts.filter((count) => count > 0).length / devotionalParts.length) * 100
      : 0,
  );

  const documentsScore = clampPercentage(
    documents.length
      ? documents.some((document) => Boolean(document.verified))
        ? 100
        : 70
      : 0,
  );

  const photosScore = clampPercentage(
    photos.length ? (photos.some((photo) => Boolean(photo.isCover)) ? 100 : 70) : 0,
  );

  const sections: CongregationProfileCompletionSections = {
    personal: personalScore,
    contacts: contactScore,
    addresses: addressScore,
    family: familyScore,
    devotional: devotionalScore,
    documents: documentsScore,
    photos: photosScore,
  };

  const overall = clampPercentage(
    Object.values(sections).reduce((total, value) => total + value, 0) /
      Object.keys(sections).length,
  );

  return {
    overall,
    sections,
  };
};

export type CongregationEligibilityRequirementStatus = 'complete' | 'attention' | 'missing';

export type CongregationEligibilityStatus = 'ready' | 'in_progress' | 'blocked';

export interface CongregationEligibilityRequirement {
  key: string;
  label: string;
  status: CongregationEligibilityRequirementStatus;
  detail: string;
}

export interface CongregationEligibilitySnapshot {
  score: number;
  status: CongregationEligibilityStatus;
  summary: string;
  requirements: CongregationEligibilityRequirement[];
  safeguardingHold: boolean;
}

const calculateRequirementStatus = (
  hasValue: boolean,
  isComplete: boolean,
  completeDetail: string,
  attentionDetail: string,
  missingDetail: string,
  label: string,
  key: string,
): CongregationEligibilityRequirement => {
  if (isComplete) {
    return { key, label, status: 'complete', detail: completeDetail };
  }
  if (hasValue) {
    return { key, label, status: 'attention', detail: attentionDetail };
  }
  return { key, label, status: 'missing', detail: missingDetail };
};

const countScoreForStatus = (status: CongregationEligibilityRequirementStatus): number => {
  if (status === 'complete') return 1;
  if (status === 'attention') return 0.5;
  return 0;
};

export const buildEligibilitySnapshot = (
  profile?: CongregationProfileSnapshot | null,
): CongregationEligibilitySnapshot => {
  if (!profile) {
    const requirements: CongregationEligibilityRequirement[] = [
      {
        key: 'contact',
        label: 'Verified contact information',
        status: 'missing',
        detail: 'Add and verify a phone number or email so mentors can reach you.',
      },
      {
        key: 'address',
        label: 'Current address on file',
        status: 'missing',
        detail: 'Provide your residential address to support household visits.',
      },
      {
        key: 'chanting',
        label: 'Chanting history logged',
        status: 'missing',
        detail: 'Track daily rounds so mentors can review your consistency.',
      },
      {
        key: 'courses',
        label: 'Devotional courses completed',
        status: 'missing',
        detail: 'Record completed courses and upload proof for verification.',
      },
      {
        key: 'documents',
        label: 'Supporting documents uploaded',
        status: 'missing',
        detail: 'Upload key documents (PAN, recommendation letters) for review.',
      },
      {
        key: 'family',
        label: 'Family or household links confirmed',
        status: 'missing',
        detail: 'Link household members or invite relatives for safeguarding awareness.',
      },
    ];
    return {
      score: 0,
      status: 'blocked',
      summary: 'Update your profile to begin eligibility review.',
      requirements,
      safeguardingHold: false,
    };
  }

  const contacts = profile.contacts ?? [];
  const addresses = profile.addresses ?? [];
  const family = profile.family ?? [];
  const chanting = profile.chanting ?? [];
  const courses = profile.courses ?? [];
  const documents = profile.documents ?? [];

  const hasVerifiedContact = contacts.some((contact) => Boolean(contact.verifiedAt));
  const hasContact = contacts.length > 0;

  const hasAddress = addresses.length > 0;

  const confirmedFamily = family.filter(
    (relation) => relation.status?.toUpperCase() === 'CONFIRMED',
  );
  const hasFamily = family.length > 0;
  const safeguardingHold = family.some(
    (relation) => relation.visibility?.toUpperCase() === 'TEMPLE_ONLY' || relation.visibility?.toUpperCase() === 'TEMPLE',
  );

  const hasStrongChanting = chanting.some((record) => getFiniteRounds(record.rounds) >= 16);
  const hasChanting = chanting.length > 0;

  const verifiedCourses = courses.filter((course) => Boolean(course.verified || course.verifiedOn));
  const hasCourses = courses.length > 0;

  const verifiedDocuments = documents.filter((document) => Boolean(document.verified || document.verifiedOn));
  const hasDocuments = documents.length > 0;

  const requirements: CongregationEligibilityRequirement[] = [
    calculateRequirementStatus(
      hasContact,
      hasVerifiedContact,
      'At least one contact method is verified for mentor outreach.',
      'Verify your phone number or email so mentors can reach you quickly.',
      'Add and verify a phone number or email so mentors can reach you.',
      'Verified contact information',
      'contact',
    ),
    calculateRequirementStatus(
      hasAddress,
      hasAddress,
      'Your address is on file for in-person visits.',
      'Provide your residential address to support household visits.',
      'Provide your residential address to support household visits.',
      'Current address on file',
      'address',
    ),
    hasStrongChanting
      ? {
          key: 'chanting',
          label: 'Consistent chanting logged',
          status: 'complete',
          detail: 'Great! Your logged rounds show steady chanting progress.',
        }
      : hasChanting
      ? {
          key: 'chanting',
          label: 'Consistent chanting logged',
          status: 'attention',
          detail: 'Keep recording at least 16 rounds per day to meet initiation expectations.',
        }
      : {
          key: 'chanting',
          label: 'Consistent chanting logged',
          status: 'missing',
          detail: 'Track daily rounds so mentors can review your consistency.',
        },
    calculateRequirementStatus(
      hasCourses,
      hasCourses && verifiedCourses.length === courses.length,
      'Required devotional courses are verified.',
      'Verify remaining courses to reach 100% completion.',
      'Record completed courses and upload proof for verification.',
      'Devotional courses completed',
      'courses',
    ),
    calculateRequirementStatus(
      hasDocuments,
      hasDocuments && verifiedDocuments.length === documents.length,
      'Supporting documents are verified.',
      'Upload or verify outstanding documents for mentor review.',
      'Upload key documents (PAN, recommendation letters) for review.',
      'Supporting documents uploaded',
      'documents',
    ),
    hasFamily
      ? calculateRequirementStatus(
          hasFamily,
          confirmedFamily.length > 0,
          'Household links are confirmed for safeguarding awareness.',
          'Confirm pending invitations so mentors know who lives with you.',
          'Link household members or invite relatives for safeguarding awareness.',
          'Family or household links confirmed',
          'family',
        )
      : {
          key: 'family',
          label: 'Family or household links confirmed',
          status: 'attention',
          detail: 'Link household members or invite relatives for safeguarding awareness.',
        },
  ];

  const rawScore = requirements.reduce((total, requirement) => total + countScoreForStatus(requirement.status), 0);
  const score = Math.round((rawScore / requirements.length) * 100);

  let status: CongregationEligibilityStatus;
  if (score >= 80 && requirements.every((requirement) => requirement.status !== 'missing')) {
    status = 'ready';
  } else if (score >= 40) {
    status = 'in_progress';
  } else {
    status = 'blocked';
  }

  const summary: string =
    status === 'ready'
      ? 'You are ready for mentor review. Keep your records current.'
      : status === 'in_progress'
      ? 'Great progress! A few items still need attention.'
      : 'Complete the basics to unlock congregation readiness.';

  return {
    score,
    status,
    summary,
    requirements,
    safeguardingHold,
  };
};

const DAY_IN_MS = 24 * 60 * 60 * 1000;

const normalizeDateKey = (value: Date): string => {
  const year = value.getUTCFullYear();
  const month = `${value.getUTCMonth() + 1}`.padStart(2, '0');
  const day = `${value.getUTCDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export interface CongregationChantingMetrics {
  currentStreak: number;
  longestStreak: number;
  lastEntryDate: string | null;
  totalRounds: number;
}

const getFiniteRounds = (value: unknown): number => {
  if (typeof value === 'number') {
    if (Number.isNaN(value) || !Number.isFinite(value) || value < 0) {
      return 0;
    }
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value.trim());
    if (Number.isNaN(parsed) || !Number.isFinite(parsed) || parsed < 0) {
      return 0;
    }
    return parsed;
  }
  return 0;
};

const expandRecordDates = (record: CongregationChantingRecord): string[] => {
  const dates: string[] = [];
  const start = record.startDate ? new Date(record.startDate) : record.endDate ? new Date(record.endDate) : null;
  if (!start) {
    return dates;
  }
  const end = record.endDate ? new Date(record.endDate) : start;
  let cursor = start.getTime();
  const limit = end.getTime();
  while (cursor <= limit) {
    dates.push(normalizeDateKey(new Date(cursor)));
    cursor += DAY_IN_MS;
  }
  return dates;
};

export const calculateChantingMetrics = (
  records: CongregationChantingRecord[] | undefined | null,
): CongregationChantingMetrics => {
  if (!records?.length) {
    return { currentStreak: 0, longestStreak: 0, lastEntryDate: null, totalRounds: 0 };
  }

  const daySet = new Set<string>();
  let totalRounds = 0;
  records.forEach((record) => {
    totalRounds += getFiniteRounds(record.rounds);
    expandRecordDates(record).forEach((key) => daySet.add(key));
  });

  if (!daySet.size) {
    return { currentStreak: 0, longestStreak: 0, lastEntryDate: null, totalRounds };
  }

  const sortedDays = Array.from(daySet).sort();
  const lastEntryDate = sortedDays[sortedDays.length - 1] ?? null;

  let longestStreak = 1;
  let currentRun = 1;
  for (let index = 1; index < sortedDays.length; index += 1) {
    const previous = Date.parse(sortedDays[index - 1]);
    const current = Date.parse(sortedDays[index]);
    if (current - previous === DAY_IN_MS) {
      currentRun += 1;
      longestStreak = Math.max(longestStreak, currentRun);
    } else {
      currentRun = 1;
    }
  }

  const todayKey = normalizeDateKey(new Date());
  let currentStreak = 0;
  let cursor = Date.parse(todayKey);
  while (daySet.has(normalizeDateKey(new Date(cursor)))) {
    currentStreak += 1;
    cursor -= DAY_IN_MS;
  }

  return {
    currentStreak,
    longestStreak,
    lastEntryDate,
    totalRounds,
  };
};

const toTimestamp = (value?: string | null): number => {
  if (!value) return 0;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

export interface MentorDevoteeInsight {
  memberId: number;
  displayName: string;
  chantingAverage: number | null;
  chantingCurrentStreak: number;
  chantingLongestStreak: number;
  chantingLastEntry: string | null;
  chantingLastEntryDaysAgo: number | null;
  totalChantingRounds: number;
  pendingCourses: number;
  verifiedCourses: number;
  totalCourses: number;
  pendingDocuments: number;
  verifiedDocuments: number;
  totalDocuments: number;
  lastActivityAt: string | null;
  restrictedVisibility: boolean;
  needsVerification: boolean;
  harinamTargetRounds: number | null;
  harinamRoundsGap: number | null;
  needsHarinamRounds: boolean;
  requiredCourseCount: number | null;
  requiredDocumentCount: number | null;
  needsCourses: boolean;
  needsDocuments: boolean;
  pendingMentorReview: boolean;
  eligibilityScore: number;
  eligibilityStatus: CongregationEligibilityStatus;
  eligibilitySummary: string;
}

export const summarizeMentorDevoteeProfile = (
  profile?: CongregationProfileSnapshot | null,
  eligibilityConfig?: CongregationEligibilityConfig | null,
): MentorDevoteeInsight | null => {
  if (!profile?.member) {
    return null;
  }

  const member = profile.member;
  const chanting = profile.chanting ?? [];
  const courses = profile.courses ?? [];
  const documents = profile.documents ?? [];
  const family = profile.family ?? [];

  const eligibility = buildEligibilitySnapshot(profile);

  const chantingMetrics = calculateChantingMetrics(chanting);

  const chantingAverage = chanting.length
    ? Math.round(
        (chanting.reduce((total, record) => total + getFiniteRounds(record.rounds), 0) / chanting.length) * 10,
      ) / 10
    : null;

  const getValidThreshold = (value?: number | null): number | null => {
    if (typeof value !== 'number') return null;
    if (Number.isNaN(value)) return null;
    return value >= 0 ? value : null;
  };

  const harinamTargetRounds = getValidThreshold(eligibilityConfig?.harinamRounds);
  const requiredCourseCount = getValidThreshold(eligibilityConfig?.requiredCourses);
  const requiredDocumentCount = getValidThreshold(eligibilityConfig?.requiredDocuments);

  const totalCourses = courses.length;
  const verifiedCourses = courses.filter((course) => Boolean(course.verified || course.verifiedOn)).length;
  const pendingCourses = Math.max(totalCourses - verifiedCourses, 0);

  const totalDocuments = documents.length;
  const verifiedDocuments = documents.filter((document) => Boolean(document.verified || document.verifiedOn)).length;
  const pendingDocuments = Math.max(totalDocuments - verifiedDocuments, 0);

  const needsHarinamRounds = harinamTargetRounds != null
    ? (chantingAverage ?? 0) < harinamTargetRounds
    : !chanting.length;
  const harinamRoundsGap = harinamTargetRounds != null
    ? Math.max(0, Math.round((harinamTargetRounds - (chantingAverage ?? 0)) * 10) / 10)
    : null;

  const needsCourses = requiredCourseCount != null
    ? verifiedCourses < requiredCourseCount || totalCourses < requiredCourseCount
    : pendingCourses > 0;

  const needsDocuments = requiredDocumentCount != null
    ? verifiedDocuments < requiredDocumentCount || totalDocuments < requiredDocumentCount
    : pendingDocuments > 0;

  const timestamps: number[] = [toTimestamp(member.updatedAt), toTimestamp(member.createdAt)];
  chanting.forEach((record) => {
    timestamps.push(toTimestamp(record.updatedAt ?? record.createdAt));
  });
  courses.forEach((course) => {
    timestamps.push(toTimestamp(course.updatedAt ?? course.createdAt));
  });
  documents.forEach((document) => {
    timestamps.push(toTimestamp(document.updatedAt ?? document.createdAt));
  });

  const latestTimestamp = timestamps.reduce((max, value) => (value > max ? value : max), 0);
  const lastActivityAt = latestTimestamp ? new Date(latestTimestamp).toISOString() : null;

  const restrictedVisibility = family.some(
    (relation) => relation.visibility?.toUpperCase() === 'TEMPLE_ONLY',
  );

  const chantingLastEntryDaysAgo = chantingMetrics.lastEntryDate
    ? Math.max(
        0,
        Math.floor(
          (Date.now() - Date.parse(`${chantingMetrics.lastEntryDate}T00:00:00Z`)) / DAY_IN_MS,
        ),
      )
    : null;

  const pendingMentorReview =
    !needsHarinamRounds && !needsCourses && !needsDocuments && eligibility.status !== 'ready';

  return {
    memberId: member.id,
    displayName: member.displayName,
    chantingAverage,
    chantingCurrentStreak: chantingMetrics.currentStreak,
    chantingLongestStreak: chantingMetrics.longestStreak,
    chantingLastEntry: chantingMetrics.lastEntryDate,
    chantingLastEntryDaysAgo,
    totalChantingRounds: chantingMetrics.totalRounds,
    pendingCourses,
    verifiedCourses,
    totalCourses,
    pendingDocuments,
    verifiedDocuments,
    totalDocuments,
    lastActivityAt,
    restrictedVisibility,
    needsVerification: needsCourses || needsDocuments,
    harinamTargetRounds,
    harinamRoundsGap,
    needsHarinamRounds,
    requiredCourseCount,
    requiredDocumentCount,
    needsCourses,
    needsDocuments,
    pendingMentorReview,
    eligibilityScore: eligibility.score,
    eligibilityStatus: eligibility.status,
    eligibilitySummary: eligibility.summary,
  };
};
